import {
  AdminPanelSettings as AccessInformationIcon,
  ManageAccounts as UserManagementIcon,
  Password as PasswordAccessIcon,
  Person as PersonalDetailsIcon,
  PersonAdd as CreateUserIcon,
  PrivacyTip as PrivacyConsentIcon,
  Save as SaveIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  Link,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { AdminSectionPanel, PageShell } from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import { LoadingState } from "@/components/common/LoadingState";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { paths } from "@/routes/paths";
import StoreMultiSelect from "@/components/common/StoreMultiSelect";
import { useAuth } from "@/contexts/AuthContext";
import { Store, StoreService } from "@/services/features/StoreService";
import { User, UserService } from "@/services/features/UserService";
import { getPasswordStrength } from "@/utils/password";
import { usePermission } from "@/utils/permissionManager";
import { ActiveRoles, getRoleApiValue, getRoleDisplayName, UserRole } from "@/utils/roleManager";
import { formatStoreSelectorLabel } from "@/utils/store";

type NewUser = Omit<User, "id" | "lastLoggedIn"> & {
  password: string;
  allowedStoreValid: boolean;
  active: boolean;
  archived: boolean;
};

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  username?: string;
  password?: string;
  allowedStores?: string;
  customerId?: string;
  privacyPolicy?: string;
}

interface AddUserProps {
  mode?: "create" | "edit";
}

type PasswordAction = "unchanged" | "manual" | "resetLink";

const AddUser: React.FC<AddUserProps> = ({ mode = "create" }) => {
  const { t } = useTranslation();
  const translateRef = useRef(t);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { userId } = useParams<{ userId: string }>();
  const isEditing = mode === "edit";
  const userService = UserService.getInstance();
  const storeService = StoreService.getInstance();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  useEffect(() => {
    translateRef.current = t;
  }, [t]);

  // Authentication and permissions
  const { user: currentUser } = useAuth();
  const { isCraveUser, isCustomerAdmin } = usePermission(currentUser?.role || "");
  const isCraveUserValue = useMemo(() => isCraveUser(), [isCraveUser]);
  const isCustomerAdminValue = useMemo(() => isCustomerAdmin(), [isCustomerAdmin]);
  const isStoreManagerValue = useMemo(
    () => currentUser?.role === UserRole.STORE_ADMIN,
    [currentUser?.role],
  );

  // Determine allowed roles based on current user's role
  const getAllowedRoles = useMemo(() => {
    if (isCraveUserValue) {
      // Crave users can create any role
      return ActiveRoles;
    } else if (isCustomerAdminValue) {
      // Customer Admin can create Store Managers, Associates, and Customer Admins
      return [UserRole.CUSTOMER_ADMIN, UserRole.STORE_ADMIN, UserRole.ASSOCIATE];
    } else if (isStoreManagerValue) {
      // Store Manager can create Associates and Store Managers
      return [UserRole.STORE_ADMIN, UserRole.ASSOCIATE];
    } else {
      // No permissions
      return [];
    }
  }, [isCraveUserValue, isCustomerAdminValue, isStoreManagerValue]);

  // Determine allowed customers based on current user's role
  const getRestrictedCustomerId = useMemo(() => {
    if (isCraveUserValue) {
      // Crave users can select any customer
      return null;
    } else if (isCustomerAdminValue) {
      // Customer Admin can only create users for their own customer
      return currentUser?.customerId || null;
    } else {
      // Store Manager can select from customers they have access to (handled separately)
      return null;
    }
  }, [isCraveUserValue, isCustomerAdminValue, currentUser?.customerId]);

  // Check if user has permission to create users
  const hasCreateUserPermission = useMemo(() => {
    return isCraveUserValue || isCustomerAdminValue || isStoreManagerValue;
  }, [isCraveUserValue, isCustomerAdminValue, isStoreManagerValue]);

  // Redirect if user doesn't have permission
  useEffect(() => {
    if (!hasCreateUserPermission) {
      void navigate(`${paths.utilities.userManagement}${location.search || ""}`);
    }
  }, [hasCreateUserPermission, navigate, location.search]);

  const [formData, setFormData] = useState<NewUser>({
    customerName: "",
    firstName: "",
    lastName: "",
    email: "",
    role: "",
    username: "",
    password: "",
    allowedStores: [],
    customerId: 0,
    allowedStoreValid: true,
    active: true,
    archived: false,
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(isEditing);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
  const [isLoadingStores, setIsLoadingStores] = useState(true);
  const [hasLoadedUser, setHasLoadedUser] = useState(!isEditing);
  const isUserFieldsDisabled = isSaving || (isEditing && !hasLoadedUser);
  const isCustomerSelectionDisabled =
    isSaving || isLoadingCustomers || isCustomerAdminValue || isEditing;
  const isSecondaryCustomerSelectionDisabled =
    isSaving || isLoadingCustomers || (isEditing && !hasLoadedUser);
  const isStoreAccessLoading = isLoadingStores;
  const isStoreAccessDisabled = isSaving || isStoreAccessLoading || (isEditing && !hasLoadedUser);
  const isSubmitDisabled =
    isSaving || isLoadingStores || isLoadingCustomers || (isEditing && !hasLoadedUser);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<{ id: number; name: string }[]>([]);
  const [availableStores, setAvailableStores] = useState<Store[]>([]);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [, setFilteredStores] = useState<Store[]>([]);
  const [showPassword, setShowPassword] = useState(false); // Crave-only filter
  const [passwordAction, setPasswordAction] = useState<PasswordAction>("unchanged");
  const [groups, setGroups] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[] | null>(null);
  const [secondaryCustomerIds, setSecondaryCustomerIds] = useState<number[]>([]);
  const hasAppliedStorePreselection = useRef(false);
  const [hasInferredSecondaryCustomers, setHasInferredSecondaryCustomers] = useState(!isEditing);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    letter: false,
    number: false,
    symbol: false,
    isValid: false,
  });

  // Get available customers for Store Managers based on their allowed stores
  const getAvailableCustomersForStoreManager = useMemo(() => {
    if (!isStoreManagerValue || !currentUser?.allowedStores) {
      return [];
    }

    const availableCustomerIds = [...new Set(availableStores.map((store) => store.customerId))];

    // Filter customers to only include those the Store Manager has store access to
    return customers.filter((customer) => availableCustomerIds.includes(customer.id));
  }, [isStoreManagerValue, currentUser?.allowedStores, availableStores, customers]);

  // Determine which customers should be shown in dropdown
  const getFilteredCustomers = useMemo(() => {
    if (isCraveUserValue) {
      // Crave users see all customers
      return customers;
    } else if (isStoreManagerValue) {
      // Store Managers see customers they have store access to
      return getAvailableCustomersForStoreManager;
    } else {
      // Customer Admins see only their customer (but dropdown will be disabled anyway)
      return customers;
    }
  }, [isCraveUserValue, isStoreManagerValue, customers, getAvailableCustomersForStoreManager]);

  useEffect(() => {
    let isActive = true;

    const fetchFormData = async () => {
      setIsLoadingStores(true);
      setIsLoadingCustomers(true);
      try {
        const storesResponse = await storeService.getStores();
        const stores = storesResponse.data;
        if (!isActive) return;

        setAvailableStores(stores);
        setFilteredStores(stores);
        setIsLoadingStores(false);

        try {
          const customersResponse = await storeService.getCustomersByStores(stores);
          if (isActive) {
            setCustomers(customersResponse);
          }
        } catch (err) {
          if (isActive) {
            setError(
              err instanceof Error
                ? err.message
                : translateRef.current("settings.userManagement.errors.failedFetchCustomers"),
            );
          }
        } finally {
          if (isActive) {
            setIsLoadingCustomers(false);
          }
        }
      } catch (err) {
        if (isActive) {
          setError(
            err instanceof Error
              ? err.message
              : translateRef.current("settings.userManagement.errors.failedFetchStores"),
          );
          setIsLoadingCustomers(false);
        }
      } finally {
        if (isActive) {
          setIsLoadingStores(false);
        }
      }
    };

    void fetchFormData();
    return () => {
      isActive = false;
    };
  }, [storeService]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    if (!userId) {
      void navigate(paths.utilities.userManagement);
      return;
    }

    let isActive = true;
    const fetchUser = async () => {
      setIsLoadingUser(true);
      setHasLoadedUser(false);
      try {
        const response = await userService.getUserById(Number(userId));
        if (!isActive || !response.data) {
          return;
        }

        const user = response.data;
        setFormData({
          ...user,
          password: "",
          allowedStoreValid: true,
        });
        setHasLoadedUser(true);
      } catch (err) {
        if (isActive) {
          setError(
            err instanceof Error
              ? err.message
              : translateRef.current("settings.userManagement.errors.failedFetchUserData"),
          );
        }
      } finally {
        if (isActive) {
          setIsLoadingUser(false);
        }
      }
    };

    void fetchUser();
    return () => {
      isActive = false;
    };
  }, [isEditing, navigate, userId, userService]);

  useEffect(() => {
    if (
      !isEditing ||
      !hasLoadedUser ||
      hasInferredSecondaryCustomers ||
      availableStores.length === 0 ||
      !formData.customerId
    ) {
      return;
    }

    const allowedStoreIds = new Set(formData.allowedStores || []);
    const inferredCustomerIds = Array.from(
      new Set(
        availableStores
          .filter(
            (store) => allowedStoreIds.has(store.id) && store.customerId !== formData.customerId,
          )
          .map((store) => store.customerId),
      ),
    );

    setSecondaryCustomerIds(inferredCustomerIds);
    setHasInferredSecondaryCustomers(true);
  }, [
    availableStores,
    formData.allowedStores,
    formData.customerId,
    hasInferredSecondaryCustomers,
    hasLoadedUser,
    isEditing,
  ]);

  useEffect(() => {
    if (isEditing || hasAppliedStorePreselection.current || availableStores.length === 0) {
      return;
    }

    const requestedStoreIds = (searchParams.get("storeIds") || searchParams.get("storeId") || "")
      .split(",")
      .map((value) => Number(value))
      .filter((storeId) => Number.isInteger(storeId) && storeId > 0);
    const requestedCustomerId = Number(searchParams.get("customerId"));
    const storesFromUrl = availableStores.filter((store) => requestedStoreIds.includes(store.id));
    const customerId =
      Number.isInteger(requestedCustomerId) && requestedCustomerId > 0
        ? requestedCustomerId
        : storesFromUrl[0]?.customerId;

    if (!customerId || (getRestrictedCustomerId && customerId !== getRestrictedCustomerId)) {
      hasAppliedStorePreselection.current = true;
      return;
    }

    const selectedStoreIds = storesFromUrl
      .filter((store) => store.customerId === customerId)
      .map((store) => store.id);

    setFormData((prev) => ({
      ...prev,
      customerId,
      allowedStores: selectedStoreIds,
    }));
    setFilteredStores(availableStores.filter((candidate) => candidate.customerId === customerId));
    hasAppliedStorePreselection.current = true;
  }, [availableStores, getRestrictedCustomerId, isEditing, searchParams]);

  // Auto-select customer for Customer Admin users only
  useEffect(() => {
    if (getRestrictedCustomerId && !formData.customerId && isCustomerAdminValue) {
      setFormData((prev) => ({
        ...prev,
        customerId: getRestrictedCustomerId,
      }));
    }
  }, [getRestrictedCustomerId, formData.customerId, isCustomerAdminValue]);

  // Auto-select all stores for Customer Admin role
  useEffect(() => {
    if (!hasInferredSecondaryCustomers) {
      return;
    }

    if (formData.role === "CUSTOMER_ADMIN" && formData.customerId && availableStores.length > 0) {
      const primaryStoreIds = availableStores
        .filter((store) => store.customerId === formData.customerId)
        .map((store) => store.id);
      const secondaryStoreIds = new Set(
        availableStores
          .filter((store) => secondaryCustomerIds.includes(store.customerId))
          .map((store) => store.id),
      );
      setFormData((prev) => ({
        ...prev,
        allowedStores: [
          ...primaryStoreIds,
          ...(prev.allowedStores || []).filter((storeId) => secondaryStoreIds.has(storeId)),
        ],
      }));
    }
  }, [
    availableStores,
    formData.customerId,
    formData.role,
    hasInferredSecondaryCustomers,
    secondaryCustomerIds,
  ]);

  const handleCustomerChange = (event: SelectChangeEvent) => {
    const value = Number(event.target.value);
    setFormData((prev) => ({
      ...prev,
      customerId: value,
      allowedStores: [],
    }));
    setSecondaryCustomerIds([]);
    setSelectedGroups(null);
    setFormErrors((prev) => ({ ...prev, customerId: undefined, allowedStores: undefined }));

    const customerStores = filterToCustomerStores(value);
    setFilteredStores(customerStores);
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;

    if (!formData.firstName?.trim()) {
      errors.firstName = t("settings.userManagement.errors.firstNameRequired");
      isValid = false;
    }

    if (!formData.lastName?.trim()) {
      errors.lastName = t("settings.userManagement.errors.lastNameRequired");
      isValid = false;
    }

    if (!formData.username?.trim()) {
      errors.username = t("settings.userManagement.errors.usernameRequired");
      isValid = false;
    }

    if ((!isEditing || passwordAction === "manual") && !formData.password.trim()) {
      errors.password = t("settings.userManagement.errors.passwordRequired");
      isValid = false;
    } else if (formData.password && !passwordStrength.isValid) {
      errors.password = t("settings.userManagement.errors.passwordRequirements");
      isValid = false;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t("settings.userManagement.errors.invalidEmail");
      isValid = false;
    }

    if (!formData.role) {
      errors.role = t("settings.userManagement.errors.roleRequired");
      isValid = false;
    }

    if (!formData.customerId) {
      errors.customerId = t("settings.userManagement.errors.customerRequired");
      isValid = false;
    }

    // Only check for allowedStores if role is not CUSTOMER_ADMIN or CRAVE_ADMIN
    if (
      formData.role !== "CUSTOMER_ADMIN" &&
      formData.role !== "CRAVE_ADMIN" &&
      !formData.allowedStores?.length
    ) {
      errors.allowedStores = t("settings.userManagement.errors.allowedStoresRequired");
      isValid = false;
    }

    if (!isEditing && !privacyAccepted) {
      errors.privacyPolicy = t("settings.userManagement.errors.privacyPolicyRequired");
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleTextFieldChange =
    (field: keyof NewUser) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = event.target.value;
    setFormData((prev) => ({ ...prev, password: newPassword }));
    setPasswordStrength(getPasswordStrength(newPassword));
    setFormErrors((prev) => ({ ...prev, password: undefined }));
  };

  const handleSelectChange = (field: keyof NewUser) => (event: SelectChangeEvent) => {
    const value = event.target.value;

    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // Auto-select all stores for Customer Admin role
      if (
        field === "role" &&
        value === "CUSTOMER_ADMIN" &&
        prev.customerId &&
        availableStores.length > 0
      ) {
        const customerStores = availableStores.filter(
          (store) => store.customerId === prev.customerId,
        );
        const secondaryStoreIds = new Set(
          availableStores
            .filter((store) => secondaryCustomerIds.includes(store.customerId))
            .map((store) => store.id),
        );
        newData.allowedStores = [
          ...customerStores.map((store) => store.id),
          ...(prev.allowedStores || []).filter((storeId) => secondaryStoreIds.has(storeId)),
        ];
      }

      return newData;
    });

    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSave = async () => {
    try {
      if (!validateForm()) {
        return;
      }

      if (isEditing && passwordAction === "resetLink" && !formData.email) {
        setFormErrors((prev) => ({
          ...prev,
          email: t("settings.userManagement.errors.emailRequiredForReset"),
        }));
        return;
      }

      setIsSaving(true);
      setError(null);

      const requestData: Partial<User> & { password?: string } = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email?.trim() || undefined,
        username: formData.username.trim(),
        role: formData.role,
        customerId: formData.customerId,
        active: formData.active,
        archived: formData.archived,
      };

      if (formData.role === "CUSTOMER_ADMIN") {
        requestData.allowedStores = formData.allowedStores || [];
      } else if (formData.role !== "CRAVE_ADMIN") {
        requestData.allowedStores = formData.allowedStores || [];
      }

      if (formData.password) {
        requestData.password = formData.password;
      }

      const response = isEditing
        ? await userService.updateUser(userId || "", {
            ...requestData,
            id: Number(userId),
            role: getRoleApiValue(formData.role),
            active: formData.active,
            allowedStores:
              formData.role === "CUSTOMER_ADMIN"
                ? formData.allowedStores || []
                : requestData.allowedStores || [],
          })
        : await userService.createUser(requestData as Partial<User> & { password: string });
      if (response.data) {
        if (isEditing && passwordAction === "resetLink") {
          try {
            await userService.requestPasswordReset(formData.email.trim());
          } catch (err) {
            console.error("Password reset link request failed:", err);
            setError(t("settings.userManagement.errors.userUpdatedResetLinkFailed"));
            return;
          }
        }

        enqueueSnackbar(
          t(
            isEditing && passwordAction === "resetLink"
              ? "settings.userManagement.messages.userUpdatedAndResetLinkSent"
              : isEditing
                ? "settings.userManagement.messages.userUpdated"
                : "settings.userManagement.messages.userCreated",
          ),
          { variant: "success" },
        );
        void navigate(`${paths.utilities.userManagement}${location.search || ""}`);
      }
    } catch (err) {
      console.error("User save failed:", err);

      if (err instanceof Error) {
        const hasBackendDetail = !err.message.startsWith("API request failed:");
        setError(
          hasBackendDetail
            ? t(
                isEditing
                  ? "settings.userManagement.errors.failedUpdateUserWithServerError"
                  : "settings.userManagement.errors.failedCreateUserWithServerError",
                { error: err.message },
              )
            : t(
                isEditing
                  ? "settings.userManagement.errors.failedUpdateUser"
                  : "settings.userManagement.errors.failedCreateUser",
              ),
        );
      } else {
        setError(
          t(
            isEditing
              ? "settings.userManagement.errors.failedUpdateUser"
              : "settings.userManagement.errors.failedCreateUser",
          ),
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const filterToCustomerStores = (customerId: number) => {
    let customerStores = availableStores;
    if (customerId) {
      customerStores = availableStores.filter((store) => store.customerId === customerId);
    }
    return customerStores;
  };
  const isCustomerAdminRole = () => {
    return !!(formData && formData.role && formData.role === "CUSTOMER_ADMIN");
  };

  const isCraveAdminRole = () => {
    return !!(formData && formData.role && formData.role === "CRAVE_ADMIN");
  };

  const canManageSecondaryCustomers = isCraveUserValue && !isCraveAdminRole();
  const isCustomerAdminWithSecondaryAccess = isCustomerAdminRole() && canManageSecondaryCustomers;

  const secondaryCustomerOptions = useMemo(
    () => getFilteredCustomers.filter((customer) => customer.id !== formData.customerId),
    [formData.customerId, getFilteredCustomers],
  );

  const selectedSecondaryCustomers = useMemo(
    () => secondaryCustomerOptions.filter((customer) => secondaryCustomerIds.includes(customer.id)),
    [secondaryCustomerIds, secondaryCustomerOptions],
  );

  const secondaryCustomerIdSet = useMemo(
    () => new Set(secondaryCustomerIds),
    [secondaryCustomerIds],
  );

  const automaticPrimaryStoreIds = useMemo(
    () =>
      formData.role === "CUSTOMER_ADMIN" && formData.customerId
        ? availableStores
            .filter((store) => store.customerId === formData.customerId)
            .map((store) => store.id)
        : [],
    [availableStores, formData.customerId, formData.role],
  );

  const handleSecondaryCustomersChange = (nextCustomers: { id: number; name: string }[]) => {
    const nextIds = nextCustomers.map((customer) => customer.id);
    const nextIdSet = new Set(nextIds);
    const permittedStoreIds = new Set(
      availableStores
        .filter(
          (store) => store.customerId === formData.customerId || nextIdSet.has(store.customerId),
        )
        .map((store) => store.id),
    );

    setSecondaryCustomerIds(nextIds);
    setFormData((prev) => ({
      ...prev,
      allowedStores: (prev.allowedStores || []).filter((storeId) => permittedStoreIds.has(storeId)),
    }));
  };

  const baseStores = useMemo(() => {
    if (!formData.customerId) return availableStores;

    return availableStores.filter(
      (store) =>
        store.customerId === formData.customerId || secondaryCustomerIdSet.has(store.customerId),
    );
  }, [availableStores, formData.customerId, secondaryCustomerIdSet]);

  const selectableStores = useMemo(
    () =>
      isCustomerAdminWithSecondaryAccess
        ? baseStores.filter((store) => secondaryCustomerIdSet.has(store.customerId))
        : baseStores,
    [baseStores, isCustomerAdminWithSecondaryAccess, secondaryCustomerIdSet],
  );

  useEffect(() => {
    const uniq = Array.from(
      new Set(selectableStores.map((store) => ((store as any).group ?? "") as string)),
    ).sort((a, b) => a.localeCompare(b));
    setGroups(uniq);
    setSelectedGroups((prev) => {
      if (prev == null) return prev;
      const filtered = prev.filter((group) => uniq.includes(group));
      const currKey = prev.slice().sort().join("|");
      const nextKey = filtered.slice().sort().join("|");
      return currKey === nextKey ? prev : filtered;
    });
  }, [selectableStores]);

  const storesForSelect = useMemo(() => {
    if (!selectedGroups || selectedGroups.length === 0) return selectableStores;
    const selectedGroupSet = new Set(selectedGroups);
    return selectableStores.filter((store) =>
      selectedGroupSet.has(((store as any).group as string | undefined) || ""),
    );
  }, [selectableStores, selectedGroups]);

  const handleGroupChange = (nextGroups: string[]) => {
    setSelectedGroups(nextGroups);

    if (nextGroups.length === 0) {
      return;
    }

    const selectedGroupSet = new Set(nextGroups);
    const visibleStoreIds = new Set(
      selectableStores
        .filter((store) => selectedGroupSet.has(((store as any).group as string | undefined) || ""))
        .map((store) => store.id),
    );

    setFormData((prev) => ({
      ...prev,
      allowedStores: (prev.allowedStores || []).filter(
        (storeId) => automaticPrimaryStoreIds.includes(storeId) || visibleStoreIds.has(storeId),
      ),
    }));
  };

  const handleStoreChange = (nextStores: Store[]) => {
    const ids = nextStores.map((store) => store.id);

    setFormData((prev) => ({
      ...prev,
      allowedStores: [...automaticPrimaryStoreIds, ...ids],
    }));

    if (ids.length > 0) {
      setFormErrors((prev) => ({ ...prev, allowedStores: undefined }));
    }
  };

  const buildStoreSelector = () => {
    // Crave Admins have global access; Customer Admins managed by customer roles retain primary-only access.
    if (isCraveAdminRole() || (isCustomerAdminRole() && !canManageSecondaryCustomers)) {
      const isCrave = isCraveAdminRole();
      const storeCount = isCrave ? availableStores.length : formData.allowedStores?.length || 0;
      return (
        <Grid item xs={12} md={12}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: { xs: 1.5, sm: 2 },
              bgcolor: "grey.50",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {isCrave
                  ? t("settings.userManagement.sections.storeAccessCraveAdmin")
                  : t("settings.userManagement.sections.storeAccessCustomerAdmin")}
              </Typography>
              <Box sx={{ position: "relative", minHeight: isStoreAccessLoading ? 92 : undefined }}>
                <Box sx={{ visibility: isStoreAccessLoading ? "hidden" : "visible" }}>
                  {storeCount > 0 && (
                    <Alert
                      severity={isCrave ? "info" : "success"}
                      sx={{ mt: 2, "& .MuiAlert-message": { width: "100%" } }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {isCrave
                          ? t("settings.userManagement.summary.storesAcrossAllCustomers", {
                              count: storeCount,
                            })
                          : t("settings.userManagement.summary.storesAutomaticallySelected", {
                              count: storeCount,
                            })}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {isCrave
                          ? t("settings.userManagement.text.craveAdminStoreAccess")
                          : t("settings.userManagement.text.customerAdminStoreAccess")}
                      </Typography>
                    </Alert>
                  )}
                </Box>

                {isStoreAccessLoading && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <LoadingState label={t("common.loading")} />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
      );
    }

    const selectedStoreObjects = storesForSelect.filter((s) =>
      (formData.allowedStores || []).includes(s.id),
    );

    return (
      <Grid item xs={12} md={12}>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "grey.50",
            p: { xs: 1.5, sm: 2 },
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
            {t("settings.userManagement.text.storeAccess")}
          </Typography>

          <Box sx={{ position: "relative", minHeight: isStoreAccessLoading ? 224 : undefined }}>
            <Box sx={{ visibility: isStoreAccessLoading ? "hidden" : "visible" }}>
              <Grid container spacing={2}>
                {isCustomerAdminWithSecondaryAccess && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ alignItems: "center" }}>
                      {t("settings.userManagement.text.customerAdminStoreAccess")}
                    </Alert>
                  </Grid>
                )}
                {!isCustomerAdminWithSecondaryAccess && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ alignItems: "center" }}>
                      {t("settings.userManagement.helperText.storeSelectionGuidance")}
                    </Alert>
                  </Grid>
                )}

                {/* Group selector (multi) */}
                <Grid item xs={12} md={12}>
                  <StoreMultiSelect
                    className="w-full min-w-0 flex-auto"
                    options={groups}
                    value={selectedGroups ?? []}
                    onChange={handleGroupChange}
                    disabled={isStoreAccessDisabled}
                    label={t("settings.userManagement.fields.groups")}
                    getOptionLabel={(g) =>
                      g === "" ? t("settings.userManagement.option.ungrouped") : g
                    }
                    getOptionKey={(g) => g}
                    emptyText={t("settings.userManagement.empty.noGroups")}
                  />
                </Grid>

                {/* Store selector (multi) */}
                <Grid item xs={12} md={12}>
                  <StoreMultiSelect
                    className="w-full min-w-0 flex-auto"
                    options={storesForSelect}
                    value={selectedStoreObjects}
                    onChange={handleStoreChange}
                    label={t("settings.userManagement.fields.stores")}
                    disabled={isStoreAccessDisabled}
                    getOptionLabel={(s) =>
                      formatStoreSelectorLabel(s, t, currentUser?.role, "id-name")
                    }
                    getOptionKey={(s) => s.id}
                    emptyText={t("settings.userManagement.empty.noStoresFound")}
                  />
                </Grid>
              </Grid>

              {formErrors.allowedStores && (
                <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>
                  {formErrors.allowedStores}
                </Typography>
              )}
            </Box>

            {isStoreAccessLoading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LoadingState label={t("common.loading")} />
              </Box>
            )}
          </Box>
        </Box>
      </Grid>
    );
  };

  const saveActionLabel = isEditing
    ? passwordAction === "manual"
      ? "settings.userManagement.actions.saveAndSetPassword"
      : passwordAction === "resetLink"
        ? "settings.userManagement.actions.saveAndSendResetLink"
        : "settings.userManagement.actions.save"
    : "settings.userManagement.actions.createUser";

  const passwordStrengthCriteria = [
    {
      isMet: passwordStrength.length,
      label: t("settings.userManagement.passwordCriteria.min8Chars"),
    },
    {
      isMet: passwordStrength.letter,
      label: t("settings.userManagement.passwordCriteria.letters"),
    },
    {
      isMet: passwordStrength.number,
      label: t("settings.userManagement.passwordCriteria.numbers"),
    },
    {
      isMet: passwordStrength.symbol,
      label: t("settings.userManagement.passwordCriteria.symbols"),
    },
  ];

  const passwordAccessPanel = isEditing ? (
    <AdminSectionPanel
      title={t("settings.userManagement.sections.passwordAccess")}
      icon={<PasswordAccessIcon />}
      paperSx={{ mt: 4 }}
    >
      <FormControl fullWidth>
        <RadioGroup
          value={passwordAction}
          onChange={(event) => {
            const nextAction = event.target.value as PasswordAction;
            setPasswordAction(nextAction);
            setFormErrors((prev) => ({ ...prev, password: undefined }));

            if (nextAction !== "manual") {
              setFormData((prev) => ({ ...prev, password: "" }));
              setPasswordStrength({
                length: false,
                letter: false,
                number: false,
                symbol: false,
                isValid: false,
              });
            }
          }}
        >
          <FormControlLabel
            value="unchanged"
            control={<Radio />}
            disabled={isUserFieldsDisabled}
            label={t("settings.userManagement.passwordActions.unchanged")}
          />
          <FormControlLabel
            value="manual"
            control={<Radio />}
            disabled={isUserFieldsDisabled}
            label={t("settings.userManagement.passwordActions.manual")}
          />
          <FormControlLabel
            value="resetLink"
            control={<Radio />}
            disabled={isUserFieldsDisabled || !formData.email}
            label={t("settings.userManagement.passwordActions.resetLink")}
          />
        </RadioGroup>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {passwordAction === "resetLink"
            ? t("settings.userManagement.passwordActions.resetLinkDescription")
            : !formData.email
              ? t("settings.userManagement.passwordActions.resetLinkEmailRequired")
              : t("settings.userManagement.passwordActions.unchangedDescription")}
        </Typography>
      </FormControl>

      {passwordAction === "manual" && (
        <Box sx={{ mt: 3 }}>
          <TextField
            fullWidth
            type={showPassword ? "text" : "password"}
            label={t("settings.userManagement.fields.newPassword")}
            value={formData.password}
            onChange={handlePasswordChange}
            error={!!formErrors.password}
            helperText={formErrors.password}
            required
            disabled={isUserFieldsDisabled}
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            InputProps={{
              endAdornment: (
                <IconButton
                  aria-label={
                    showPassword
                      ? t("settings.userManagement.actions.hide")
                      : t("settings.userManagement.actions.show")
                  }
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isUserFieldsDisabled}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              mt: 1.5,
            }}
          >
            {passwordStrengthCriteria.map((criterion, index) => (
              <React.Fragment key={criterion.label}>
                {index > 0 && (
                  <Typography variant="caption" color="text.disabled">
                    •
                  </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: criterion.isMet ? "success.main" : "grey.300",
                    }}
                  />
                  <Typography
                    variant="caption"
                    color={criterion.isMet ? "success.main" : "text.secondary"}
                  >
                    {criterion.label}
                  </Typography>
                </Box>
              </React.Fragment>
            ))}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {t("settings.userManagement.passwordCriteria.includeAtLeast2")}
            </Typography>
          </Box>
        </Box>
      )}
    </AdminSectionPanel>
  ) : null;

  if (!hasCreateUserPermission) {
    return (
      <PageShell
        title={t(
          isEditing
            ? "settings.userManagement.title.editUser"
            : "settings.userManagement.title.addUser",
        )}
        icon={<UserManagementIcon />}
      >
        <Box sx={{ p: { xs: 2, sm: 3, md: 5 } }}>
          <Alert severity="error">{t("settings.userManagement.errors.noCreatePermission")}</Alert>
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={t(
        isEditing
          ? "settings.userManagement.title.editUser"
          : "settings.userManagement.title.addUser",
      )}
      icon={<UserManagementIcon />}
      titleAccessory={
        <LoadingRing loading={isLoadingStores || isLoadingCustomers || isLoadingUser} />
      }
    >
      <Box
        component="form"
        id="add-user-form"
        noValidate
        autoComplete="off"
        aria-busy={isSaving}
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        sx={{
          position: "relative",
          pb: { xs: 10, sm: 10 },
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={4}>
              {/* Personal Details */}
              <Grid item xs={12} lg={6}>
                <AdminSectionPanel
                  title={t("settings.userManagement.sections.personalDetails")}
                  icon={<PersonalDetailsIcon />}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("settings.userManagement.fields.firstName")}
                        value={formData.firstName}
                        onChange={handleTextFieldChange("firstName")}
                        error={!!formErrors.firstName}
                        helperText={formErrors.firstName}
                        required
                        disabled={isUserFieldsDisabled}
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("settings.userManagement.fields.lastName")}
                        value={formData.lastName}
                        onChange={handleTextFieldChange("lastName")}
                        error={!!formErrors.lastName}
                        helperText={formErrors.lastName}
                        required
                        disabled={isUserFieldsDisabled}
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t("settings.userManagement.fields.username")}
                        value={formData.username}
                        onChange={handleTextFieldChange("username")}
                        error={!!formErrors.username}
                        helperText={formErrors.username}
                        required
                        disabled={isUserFieldsDisabled}
                        autoComplete="off"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        inputMode="text"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t("settings.userManagement.fields.email")}
                        type="email"
                        value={
                          isEditing && !formData.email
                            ? t("settings.userManagement.fields.noEmailAddress")
                            : formData.email
                        }
                        onChange={handleTextFieldChange("email")}
                        error={!!formErrors.email}
                        helperText={
                          isEditing
                            ? t("settings.userManagement.helperText.emailCannotChange")
                            : formErrors.email
                        }
                        disabled={isUserFieldsDisabled || isEditing}
                        autoComplete="off"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        inputMode="email"
                      />
                    </Grid>
                    {!isEditing && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type={showPassword ? "text" : "password"}
                          label={t("settings.userManagement.fields.password")}
                          value={formData.password}
                          onChange={handlePasswordChange}
                          error={!!formErrors.password}
                          helperText={formErrors.password}
                          required
                          disabled={isUserFieldsDisabled}
                          autoComplete="off"
                          autoCapitalize="none"
                          autoCorrect="off"
                          spellCheck={false}
                          InputProps={{
                            endAdornment: (
                              <IconButton
                                aria-label={
                                  showPassword
                                    ? t("settings.userManagement.actions.hide")
                                    : t("settings.userManagement.actions.show")
                                }
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={isUserFieldsDisabled}
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            ),
                          }}
                        />
                        <Box sx={{ mt: 1.5 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: passwordStrength.length ? "success.main" : "grey.300",
                                }}
                              />
                              <Typography
                                variant="caption"
                                color={passwordStrength.length ? "success.main" : "text.secondary"}
                              >
                                {t("settings.userManagement.passwordCriteria.min8Chars")}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.disabled">
                              •
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: passwordStrength.letter ? "success.main" : "grey.300",
                                }}
                              />
                              <Typography
                                variant="caption"
                                color={passwordStrength.letter ? "success.main" : "text.secondary"}
                              >
                                {t("settings.userManagement.passwordCriteria.letters")}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.disabled">
                              •
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: passwordStrength.number ? "success.main" : "grey.300",
                                }}
                              />
                              <Typography
                                variant="caption"
                                color={passwordStrength.number ? "success.main" : "text.secondary"}
                              >
                                {t("settings.userManagement.passwordCriteria.numbers")}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.disabled">
                              •
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: passwordStrength.symbol ? "success.main" : "grey.300",
                                }}
                              />
                              <Typography
                                variant="caption"
                                color={passwordStrength.symbol ? "success.main" : "text.secondary"}
                              >
                                {t("settings.userManagement.passwordCriteria.symbols")}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              {t("settings.userManagement.passwordCriteria.includeAtLeast2")}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </AdminSectionPanel>
                {passwordAccessPanel}
              </Grid>

              {/* Access Information */}
              <Grid item xs={12} lg={6}>
                <AdminSectionPanel
                  title={t("settings.userManagement.sections.accessInformation")}
                  icon={<AccessInformationIcon />}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <MuiAutocompleteMultiSelect
                        label={t("settings.userManagement.fields.role")}
                        options={getAllowedRoles}
                        value={formData.role ? [formData.role] : []}
                        onChange={(next) => {
                          const role = next[0];
                          if (role) {
                            handleSelectChange("role")({
                              target: { value: role },
                            } as SelectChangeEvent);
                          }
                        }}
                        getOptionLabel={(option) => getRoleDisplayName(option)}
                        getOptionKey={(option) => option}
                        required
                        error={!!formErrors.role}
                        helperText={formErrors.role}
                        disabled={isUserFieldsDisabled}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required error={!!formErrors.customerId}>
                        <MuiAutocompleteMultiSelect
                          label={t("settings.userManagement.fields.customerAccount")}
                          options={getFilteredCustomers}
                          value={getFilteredCustomers.filter(
                            (customer) => customer.id === formData.customerId,
                          )}
                          onChange={(next) => {
                            const customer = next[0];
                            if (customer) {
                              handleCustomerChange({
                                target: { value: String(customer.id) },
                              } as SelectChangeEvent);
                            }
                          }}
                          disabled={isCustomerSelectionDisabled}
                          loading={isLoadingCustomers}
                          getOptionLabel={(option) => option.name}
                          getOptionKey={(option) => option.id}
                          required
                          error={!!formErrors.customerId}
                          helperText={formErrors.customerId}
                        />
                        {isCustomerAdminValue && (
                          <Typography color="text.secondary" variant="caption" sx={{ mt: 1 }}>
                            {t(
                              "settings.userManagement.helperText.customerAccountAutomaticallySet",
                            )}
                          </Typography>
                        )}
                        {isStoreManagerValue && getFilteredCustomers.length === 0 && (
                          <Typography color="text.secondary" variant="caption" sx={{ mt: 1 }}>
                            {t("settings.userManagement.text.noCustomersAvailableBased")}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    {canManageSecondaryCustomers && (
                      <Grid item xs={12}>
                        <MuiAutocompleteMultiSelect
                          label={t("settings.userManagement.fields.secondaryCustomers")}
                          options={secondaryCustomerOptions}
                          value={selectedSecondaryCustomers}
                          onChange={handleSecondaryCustomersChange}
                          multiSelect
                          disabled={isSecondaryCustomerSelectionDisabled}
                          loading={isLoadingCustomers}
                          getOptionLabel={(option) => option.name}
                          getOptionKey={(option) => option.id}
                        />
                      </Grid>
                    )}

                    {buildStoreSelector()}
                  </Grid>
                </AdminSectionPanel>

                {!isEditing && (
                  <AdminSectionPanel
                    title={t("settings.userManagement.sections.privacyConsent")}
                    icon={<PrivacyConsentIcon />}
                    paperSx={{ mt: 4 }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={privacyAccepted}
                          disabled={isSaving}
                          onChange={(e) => {
                            const accepted = e.target.checked;
                            setPrivacyAccepted(accepted);
                            if (accepted) {
                              setFormErrors((prev) => ({ ...prev, privacyPolicy: undefined }));
                            }
                          }}
                          size={isMobile ? "small" : "medium"}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {t("settings.userManagement.text.privacyPolicyConsentPrefix")}{" "}
                          <Link
                            href="https://craveretail.com/privacy/"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textDecoration: "none" }}
                          >
                            {t("auth.text.privacyPolicy")}
                          </Link>
                          {t("settings.userManagement.text.privacyPolicyConsentSuffix")}
                        </Typography>
                      }
                    />
                    {formErrors.privacyPolicy && (
                      <Typography color="error" variant="caption" sx={{ display: "block", mt: 1 }}>
                        {formErrors.privacyPolicy}
                      </Typography>
                    )}
                  </AdminSectionPanel>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Desktop sticky actions */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            position: "sticky",
            bottom: 0,
            zIndex: (theme) => theme.zIndex.modal + 1,
            justifyContent: "flex-end",
            gap: 1.5,
            px: 0,
            py: 2,
            bgcolor: "transparent",
            borderTop: "none",
            borderColor: "divider",
            boxShadow: "none",
          }}
        >
          <Button
            variant="contained"
            onClick={(e) => {
              e.preventDefault();
              handleSave();
            }}
            disabled={isSubmitDisabled}
            size="large"
            type="submit"
            startIcon={isSaving ? undefined : isEditing ? <SaveIcon /> : <CreateUserIcon />}
          >
            {isSaving ? <CircularProgress size={24} /> : t(saveActionLabel)}
          </Button>
        </Box>

        {/* Mobile sticky actions */}
        <Box
          sx={{
            display: { xs: "flex", sm: "none" },
            position: "sticky",
            bottom: 0,
            zIndex: (theme) => theme.zIndex.modal + 1,
            justifyContent: "flex-end",
            gap: 1,
            px: 0,
            py: 1.5,
            bgcolor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
            boxShadow: "0 -4px 10px rgba(0,0,0,0.06)",
          }}
        >
          <Button
            variant="contained"
            onClick={(e) => {
              e.preventDefault();
              handleSave();
            }}
            disabled={isSubmitDisabled}
            size="small"
            type="submit"
            startIcon={isSaving ? undefined : isEditing ? <SaveIcon /> : <CreateUserIcon />}
          >
            {isSaving ? <CircularProgress size={20} /> : t(saveActionLabel)}
          </Button>
        </Box>
      </Box>
    </PageShell>
  );
};

export default AddUser;
